// ── Step3 ViewModel — Pure DOM reads + snapshot helpers ───────────
// No DOM writes. No API calls. Only state reads and helper logic.

import {
  getState,
  setState,
} from '../../../state.js';
import {
  escapeHtml,
  normalizeText,
  multilineHtml,
  formatVolume,
} from '../../../utils.js';
import {
  getCompletedBatchSnapshots,
} from '../../../listing-utils.js';

// ── DOM Helpers ─────────────────────────────────────────────────

export const $ = (sel) => document.querySelector(sel);
export const $$ = (sel) => document.querySelectorAll(sel);

// ── Snapshot Helpers ────────────────────────────────────────────

export function resolveSnapshotImageUrl(snapshot) {
  if (!snapshot) return '';
  if (typeof snapshot.imageUrl === 'string' && snapshot.imageUrl.trim()) {
    return snapshot.imageUrl.trim();
  }
  const m = snapshot.mcpAsin;
  if (!m || typeof m !== 'object') return '';
  const candidates = [
    m.imageUrl,
    m.imgUrl,
    m.mainImage,
    m.image,
    m.picUrl,
    Array.isArray(m.images) ? m.images[0] : null,
    Array.isArray(m.imageList) ? m.imageList[0] : null,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return '';
}

export function getBatchSnapshotsList() {
  const batch = getState('batchSnapshots');
  if (Array.isArray(batch) && batch.length) return getCompletedBatchSnapshots(batch);
  const one = getState('snapshotState');
  return one ? getCompletedBatchSnapshots([one]) : [];
}

export function getIncludedCompetitorAsins() {
  const list = getBatchSnapshotsList();
  const included = getState('competitorAsinsIncluded');
  if (included instanceof Set && included.size) return included;
  return new Set(list.map((snap) => snap.asin));
}

export function selectCompetitorView(index) {
  const list = getBatchSnapshotsList();
  if (!list.length) return;
  const idx = Math.max(0, Math.min(index, list.length - 1));
  setState('selectedCompetitorIndex', idx);
  setState('snapshotState', list[idx]);
}

export function isListingReadyForStep4() {
  const meta = getState('listingGenerationMeta');
  if (meta?.readiness && meta.readiness.canProceedToFullTextReview === false) {
    return false;
  }
  const title = getState('listingDraftTitle');
  const bullets = getState('listingDraftBullets');
  const titleOk = title && !title.error && (title.titleEn || title.titleOptions?.length);
  const bulletsOk = bullets && !bullets.error && bullets.bulletsEn?.length >= 5;
  return !!(titleOk && bulletsOk);
}

// 读取 Step3 前移的确定性检查结果（合规/评分/算法）。纯 state 读取，
// 返回 null 表示旧结果/无结果，UI 应隐藏卡片（向后兼容）。
export function readDeterministicChecks(generationMeta) {
  if (!generationMeta || typeof generationMeta !== 'object') return null;
  const checks = generationMeta.mergeMeta?.deterministicChecks;
  return checks && typeof checks === 'object' ? checks : null;
}

// 读取 Step4 写回后的失效重算结论（recompute）。纯 state 读取。
export function readRecomputeInfo(generationMeta) {
  if (!generationMeta || typeof generationMeta !== 'object') {
    return { action: 'noop', needsReview: false, stale: false };
  }
  const recompute = generationMeta.mergeMeta?.recompute;
  if (!recompute || typeof recompute !== 'object') {
    return { action: 'noop', needsReview: false, stale: false };
  }
  return {
    action: String(recompute.action || 'noop'),
    needsReview: recompute.needsReview === true,
    stale: recompute.stale === true,
    changedFields: Array.isArray(recompute.changedFields) ? recompute.changedFields : [],
  };
}

// ── Format Helpers ──────────────────────────────────────────────

export function sectionLabel(section) {
  return {
    title: '标题',
    bullets: '五点',
    description: '描述',
    searchTerms: '搜索词',
    qa: 'Q&A',
  }[section] || section;
}

export function formatCountMap(counts = {}, labels = {}) {
  return Object.entries(labels)
    .map(([key, label]) => {
      const value = Number(counts?.[key] || 0);
      return value > 0 ? `<span class="opt-diag-tag gray">${escapeHtml(label)} ${value}</span>` : '';
    })
    .filter(Boolean)
    .join('');
}
